import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  useToast,
} from "@chakra-ui/react";
import { ChevronLeft, Wallet, Gift, Zap, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { createRechargeOrder } from "../../services/walletService";
import { getAppControls } from "../../services/configService";

// Amount options with their bonus percentages
const AMOUNT_OPTIONS = [
  { amount: 50, extra: 0 },
  { amount: 100, extra: 0 },
  { amount: 200, extra: 0 },
  { amount: 500, extra: 5, popular: true },
  { amount: 1000, extra: 5 },
  { amount: 2000, extra: 10 },
  { amount: 3000, extra: 10 },
  { amount: 4000, extra: 12 },
  { amount: 8000, extra: 12 },
  { amount: 15000, extra: 15 },
  { amount: 20000, extra: 15 },
  { amount: 50000, extra: 20 },
  { amount: 100000, extra: 20 },
];

// Bonus tiers for calculating next bonus threshold
const BONUS_TIERS = [
  { minAmount: 500, extra: 5 },
  { minAmount: 2000, extra: 10 },
  { minAmount: 4000, extra: 12 },
  { minAmount: 15000, extra: 15 },
  { minAmount: 50000, extra: 20 },
];

const RechargeModal = ({ isOpen, onClose, walletBalance = 0 }) => {
  const [amount, setAmount] = useState("");
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [minRechargeAmount, setMinRechargeAmount] = useState(2);
  const toast = useToast();

  // Fetch app controls on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getAppControls();
        if (response?.result?.minRechargeAmount) {
          setMinRechargeAmount(response.result.minRechargeAmount);
        }
      } catch (error) {
        console.log("Failed to fetch app controls:", error);
      }
    };
    fetchSettings();
  }, []);

  // Calculate the promotional message based on current amount
  const promoMessage = useMemo(() => {
    const currentAmount = Number(amount) || 0;

    for (const tier of BONUS_TIERS) {
      if (currentAmount < tier.minAmount) {
        const amountNeeded = tier.minAmount - currentAmount;
        return `Add ₹${amountNeeded} more to get ${tier.extra}% extra.`;
      }
    }
    return null; // Already at max tier
  }, [amount]);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    setSelectedPreset(null);
  };

  const handleQuickAdd = (addAmount) => {
    const currentAmount = Number(amount) || 0;
    const newAmount = currentAmount + addAmount;
    setAmount(String(newAmount));
    setSelectedPreset(null);
  };

  const handlePresetSelect = (preset) => {
    setAmount(String(preset.amount));
    setSelectedPreset(preset.amount);
  };

  const handleRecharge = async () => {
    if (!amount || Number(amount) < minRechargeAmount) {
      toast({
        title: "Invalid Amount",
        description: `Minimum recharge amount is ₹${minRechargeAmount}.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await createRechargeOrder(Number(amount));
      const order = response.result;

      if (order.upiLink) {
        window.open(order.upiLink, "_blank");
        toast({
          title: "Payment Link Opened",
          description: "Complete the payment in the new tab",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        onClose();
        setAmount("");
        setSelectedPreset(null);
      } else {
        toast({
          title: "Error",
          description: "Payment link not available",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.toString(),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount("");
    setSelectedPreset(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        bg="#3F234E"
        borderRadius="2xl"
        maxH="90vh"
        overflow="auto"
        border="1px solid"
        borderColor="whiteAlpha.100"
        mx={4}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
            >
              <ChevronLeft size={20} className="text-gray-400" />
            </button>
            <h2 className="text-white font-semibold text-lg">
              Add money to wallet
            </h2>
          </div>
          <div className="flex items-center gap-1.5 bg-purple-500/10 px-3 py-1.5 rounded-full border border-purple-500/20">
            <Wallet size={14} className="text-purple-400" />
            <span className="text-purple-300 text-sm font-medium">
              ₹{walletBalance}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          {/* Enter Amount Section */}
          <p className="text-gray-400 text-sm mb-1">Enter Amount</p>
          <p className="text-gray-600 text-xs mb-3">
            Minimum ₹{minRechargeAmount}
          </p>

          <div className="flex items-center gap-2 mb-5">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg font-bold">
                ₹
              </span>
              <input
                type="number"
                placeholder="0"
                value={amount}
                onChange={handleAmountChange}
                className="w-full pl-8 pr-3 py-3 text-2xl font-bold text-white bg-transparent border-b-2 border-white/10 focus:border-purple-500 outline-none transition-colors placeholder-gray-600"
              />
            </div>
            <button
              onClick={() => handleQuickAdd(50)}
              className="px-3 py-2 text-xs font-medium text-purple-300 border border-purple-500/30 rounded-full hover:bg-purple-500/10 transition-colors whitespace-nowrap"
            >
              + ₹50
            </button>
            <button
              onClick={() => handleQuickAdd(100)}
              className="px-3 py-2 text-xs font-medium text-purple-300 border border-purple-500/30 rounded-full hover:bg-purple-500/10 transition-colors whitespace-nowrap"
            >
              + ₹100
            </button>
          </div>

          {/* Promo Banner */}
          <AnimatePresence>
            {promoMessage && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-5"
              >
                <div className="flex items-center gap-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                  <Gift size={18} className="text-amber-400 flex-shrink-0" />
                  <p className="text-amber-200/80 text-sm">{promoMessage}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Amount Grid */}
          <div className="grid grid-cols-3 gap-2.5">
            {AMOUNT_OPTIONS.map((option) => {
              const isSelected = selectedPreset === option.amount;
              return (
                <button
                  key={option.amount}
                  position="relative"
                  bg={
                    isSelected
                      ? "yellow.50"
                      : option.extra > 0
                        ? "green.50"
                        : "white"
                  }
                  border="2px solid"
                  borderColor={isSelected ? "yellow.400" : "gray.200"}
                  borderRadius="lg"
                  p={4}
                  textAlign="center"
                  cursor="pointer"
                  onClick={() => handlePresetSelect(option)}
                  className={`relative p-3 rounded-xl text-center transition-all duration-200 border-2
                    ${
                      isSelected
                        ? "bg-purple-500/20 border-purple-500 shadow-lg shadow-purple-500/10"
                        : option.extra > 0
                        ? "bg-green-500/5 border-white/5 hover:border-green-500/30"
                        : "bg-[#2A1B3D]/50 border-white/5 hover:border-white/10"
                    }`}
                >
                  {option.popular && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5 whitespace-nowrap">
                      <Star size={8} fill="currentColor" />
                      Popular
                    </span>
                  )}
                  <span
                    className={`text-base font-semibold ${
                      isSelected ? "text-purple-300" : "text-white"
                    }`}
                  >
                    ₹{option.amount.toLocaleString()}
                  </span>
                  {option.extra > 0 && (
                    <p className="text-green-400 text-xs font-medium mt-0.5">
                      {option.extra}% Extra
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bottom Button */}
        <div className="p-4 pt-2 border-t border-white/5">
          <button
            onClick={handleRecharge}
            disabled={loading || !amount || Number(amount) < minRechargeAmount}
            className={`w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all
              ${
                loading || !amount || Number(amount) < minRechargeAmount
                  ? "bg-gray-600/50 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.01] active:scale-[0.99]"
              }`}
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                Processing...
              </>
            ) : (
              <>
                <Zap size={16} className="fill-current" />
                Proceed
              </>
            )}
          </button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default RechargeModal;
